/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import * as moment from "moment";
import { Moment } from "moment";
import { PeopleCountManager, PeopleCountResult } from "sh-managers";
import { ScheduleCalendarType, ScheduleCalendarTypes, ShiftTypes } from "sh-models";

import schedulesViewStateStore from "../../schedules/lib/store/store";

import { ScheduleGroupPeopleCountRowContainerProps } from "./ScheduleGroupPeopleCountRowContainerProps";
import { ScheduleGroupPeopleCountRowProps } from "./ScheduleGroupPeopleCountRowProps";

/**
 * Six hours in milliseconds.
 */
const sixHoursInMs = 21600000;

/**
 * Twelve hours in milliseconds.
 */
const twelveHoursInMs = 43200000;

/**
 * The start hours in week view. This defines the starting hours of each interval group cells.
 * In Week view, each interval group cells is made of 24 hours consisting of 4 intervals of 6 hours each.
 * Important: in case of DST time-zone hour changes, impacted interval will not be 6 hours long but adjusted to match the targeted hour (e.g., 12AM, 6AM, 12PM, 6PM).
 */
const weekViewStartHours = 0;

/**
 * The week view morning hours defines until which hour the start hours goes to.
 */
const weekViewMorningHours = 6;

/**
 * The week view afternoon hours defines until which hour the morning hours goes to.
 */
const weekViewAfternoonHours = 12;

/**
 * The week view evening hours defines until which hour the afternoon hours goes to.
 */
const weekViewEveningHours = 18;

/**
 * The start hours in fortnight view. This defines the starting hours of each interval group cells.
 * In Fortnight view, each interval group cells is made of 24 hours consisting of 2 intervals of 12 hours each.
 * Important: in case of DST time-zone hour changes, impacted interval will not be 12 hours long but adjusted to match the targeted hour (e.g., 12AM, 12PM).
 */
const fortnightViewStartHours = 0;

/**
 * The fortnight view afternoon hours defines until which hour the start hours goes to.
 */
const fortnightViewAfternoonHours = 12;

/**
 * Gets the 'ScheduleGroupCountRow' component properties based on given schedule group id.
 * @param props The properties.
 * @returns The 'ScheduleGroupCountRow' component properties.
 */
export const getScheduleGroupCountRowProps = (props: ScheduleGroupPeopleCountRowContainerProps): ScheduleGroupPeopleCountRowProps => {
    const { scheduleCalendarType, viewEndDate, viewStartDate } = schedulesViewStateStore();
    const isFortnightView = scheduleCalendarType === ScheduleCalendarTypes.Fortnight;
    const intervalMs = isFortnightView ? twelveHoursInMs : sixHoursInMs;
    const startDate = viewStartDate.clone();
    const startTimestampMs = startDate.valueOf();
    const endDate = viewEndDate.clone();
    const endTimestampMs = endDate.valueOf();
    const peopleCountsByTimestampMs = getPeopleCountsByTimestampMs(props, startTimestampMs, endTimestampMs, isFortnightView);

    return {
        cellIntervalMs: intervalMs,
        cellsTimestampsMs: getCellsTimestampsMs(scheduleCalendarType, startDate, endDate, isFortnightView),
        endTimestampMs,
        peopleCountsByTimestampMs,
        startTimestampMs
    };
};

/**
 * Gets each cell timestamp in milliseconds. It handles DST time-zone hour changes by respecting the time change and adjusting the cell timestamp accordingly.
 * @param view The schedule view.
 * @param startDate The start date.
 * @param endDate The end date.
 * @returns The cells timestamps in milliseconds.
 */
const getCellsTimestampsMs = (view: ScheduleCalendarType, startDate: Moment, endDate: Moment, isFortnightView: boolean): number[] => {
    const cellsTimestampsMs: number[] = [];
    const startTimestampMs = startDate.valueOf();
    const endTimestampMs = endDate.valueOf();
    const date = startDate.clone();
    let timestampMs = startTimestampMs;

    while (timestampMs < endTimestampMs) {
        cellsTimestampsMs.push(timestampMs);

        if (isFortnightView) {
            setNextCellDatetimeForFortnightView(date);
        } else if (view === ScheduleCalendarTypes.Week) {
            setNextCellDatetimeForWeekView(date);
        } else {
            date.add(1, "day");
        }

        timestampMs = date.valueOf();
    }

    return cellsTimestampsMs;
};

// Important: the following logic is required because moment date.add(6, "hours") does not respect DST time-zone hour changes.
const setNextCellDatetimeForWeekView = (date: Moment): void => {
    if (date.hours() < weekViewMorningHours) {
        date.hours(weekViewMorningHours);
    } else if (date.hours() < weekViewAfternoonHours) {
        date.hours(weekViewAfternoonHours);
    } else if (date.hours() < weekViewEveningHours) {
        date.hours(weekViewEveningHours);
    } else {
        date.hours(weekViewStartHours);
        date.add(1, "day");
    }
};

// Important: the following logic is required because moment date.add(12, "hours") does not respect DST time-zone hour changes.
const setNextCellDatetimeForFortnightView = (date: Moment): void => {
    if (date.hours() < fortnightViewAfternoonHours) {
        date.hours(fortnightViewAfternoonHours);
    } else {
        date.hours(fortnightViewStartHours);
        date.add(1, "day");
    }
};

const getPeopleCountsByTimestampMs = (
    props: ScheduleGroupPeopleCountRowContainerProps,
    startTimestampMs: number,
    endTimestampMs: number,
    isFortnightView: boolean
): PeopleCountResult => {
    const { memberShiftsData } = props;
    const intervalMs = isFortnightView ? twelveHoursInMs : sixHoursInMs;

    const manager = new PeopleCountManager({
        intervalMs: intervalMs,
        limits: {
            endTimestampMs,
            startTimestampMs
        }
    });

    memberShiftsData.forEach(({ member, memberShifts }) => {
        memberShifts.forEach(shift => {
            if (shift.shiftType == ShiftTypes.Working) {
                const endTimestampMs = shift.endTime.valueOf();
                const startTimestampMs = isFortnightView ? getStartIntervalTimestampMsForFortnight(shift.startTime) : getStartIntervalTimestampMs(shift.startTime);
                const date = moment(startTimestampMs);
                let timestampMs = startTimestampMs;

                while (timestampMs < endTimestampMs) {
                    manager.addTimeEntry({
                        memberId: member.id,
                        shiftHours: 1,
                        timestampMs
                    });

                    if (isFortnightView) {
                        setNextCellDatetimeForFortnightView(date);
                    } else {
                        setNextCellDatetimeForWeekView(date);
                    }
                    timestampMs = date.valueOf();
                }
            }
        });
    });

    return manager.getResult();
};

const getStartIntervalTimestampMs = (startTime: Moment): number => {
    const date = startTime.clone();
    const hours = date.hours();

    if (hours < weekViewMorningHours) {
        date.set("hours", weekViewStartHours);
    } else if (hours < weekViewAfternoonHours) {
        date.set("hours", weekViewMorningHours);
    } else if (hours < weekViewEveningHours) {
        date.set("hours", weekViewAfternoonHours);
    } else {
        date.set("hours", weekViewEveningHours);
    }

    return date.startOf("hours").valueOf();
};

/**
 * Snaps a shift start time to the beginning of its 12-hour interval for Fortnight view.
 * Fortnight view uses 2 intervals per day (12AM–12PM, 12PM–12AM) instead of
 * Week view's 4 intervals per day (12AM–6AM, 6AM–12PM, 12PM–6PM, 6PM–12AM).
 * This is used to bucket shifts into the correct staff-per-interval cell.
 */
const getStartIntervalTimestampMsForFortnight = (startTime: Moment): number => {
    const date = startTime.clone();
    const hours = date.hours();

    if (hours < fortnightViewAfternoonHours) {
        date.set("hours", fortnightViewStartHours);
    } else {
        date.set("hours", fortnightViewAfternoonHours);
    }

    return date.startOf("hours").valueOf();
};
